/**
 * useMealPlanDetails Hook
 *
 * Custom React hook for managing individual meal plan details with photo URL fetching.
 * Fetches meal plan data and creator photo URL for the meal plan details page.
 *
 * @module hooks/useMealPlanDetails
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { apiService } from '@/services/ApiService';
import { MealApiError } from '@/services/domains/MealDomain';
import type { MealPlanFrontend } from '@/types/mealPlan';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Return type for useMealPlanDetails hook
 */
export interface UseMealPlanDetailsReturn {
  /** Meal plan data with creator photo URL */
  mealPlan: MealPlanFrontend | null;
  /** Loading state indicator */
  loading: boolean;
  /** Error message if request failed */
  error: string | null;
  /** Function to refresh meal plan data */
  refresh: () => Promise<void>;
}

/**
 * Hook for managing individual meal plan details with creator photo
 *
 * @param mealPlanId - ID of the meal plan to fetch
 * @returns Object containing meal plan data, loading state, error state, and refresh function
 *
 * @example
 * ```typescript
 * const MealPlanDetailsComponent = () => {
 *   const { id } = useParams<{ id: string }>();
 *   const { mealPlan, loading, error, refresh } = useMealPlanDetails(id);
 *
 *   if (loading) return <div>Loading meal plan...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *   if (!mealPlan) return <div>Meal plan not found</div>;
 *
 *   return (
 *     <div>
 *       <h1>{mealPlan.label}</h1>
 *       <AvatarPhoto src={mealPlan.creatorPhotoUrl} />
 *       <p>Created by {mealPlan.creatorName}</p>
 *     </div>
 *   );
 * };
 * ```
 */
export const useMealPlanDetails = (mealPlanId: string | undefined): UseMealPlanDetailsReturn => {
  const logger = getLogger();
  const navigate = useNavigate();

  // State management
  const [mealPlan, setMealPlan] = useState<MealPlanFrontend | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch meal plan data from the API
   */
  const fetchMealPlan = useCallback(async () => {
    if (!mealPlanId) {
      setError('Meal plan ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setMealPlan(null); // Clear previous data to prevent flicker

      logger.log('Fetching meal plan details:', mealPlanId);
      const mealPlanData = await apiService.mealplan.getMealPlan(mealPlanId);

      // Fetch creator photo URL
      let creatorPhotoUrl: string | null = null;
      try {
        const photoResponse = await apiService.photo.getPhotoUrls({
          user_ids: [mealPlanData.creatorId]
        });
        creatorPhotoUrl = photoResponse.photo_urls[mealPlanData.creatorId] || null;
        logger.log('Creator photo URL fetched successfully:', creatorPhotoUrl);
      } catch (photoError) {
        logger.warn('Failed to fetch creator photo URL:', photoError);
        // Continue without photo - don't fail the entire request
      }

      // Add photo URL to meal plan
      const mealPlanWithPhoto = {
        ...mealPlanData,
        creatorPhotoUrl
      };

      logger.log('Meal plan fetched successfully:', mealPlanWithPhoto);
      setMealPlan(mealPlanWithPhoto);

    } catch (err) {
      logger.error('Failed to fetch meal plan:', err);

      let errorMessage = 'Failed to load meal plan details. Please try again.';

      if (err instanceof MealApiError) {
        switch (err.status) {
          case 401:
            errorMessage = 'Authentication required. Please log in to continue.';
            break;
          case 403:
            errorMessage = 'You do not have permission to view this meal plan.';
            // Redirect to dashboard on 403 error
            navigate('/dashboard', { replace: true });
            return; // Exit early to prevent setting error state
          case 404:
            errorMessage = 'Meal plan not found.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = err.message || errorMessage;
        }
      }

      setError(errorMessage);
      setMealPlan(null);
    } finally {
      setLoading(false);
    }
  }, [mealPlanId, navigate]); // Added navigate to dependencies

  /**
   * Refresh meal plan data
   */
  const refresh = useCallback(async () => {
    if (!mealPlanId) {
      setError('Meal plan ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setMealPlan(null);

      logger.log('Refreshing meal plan details:', mealPlanId);
      const mealPlanData = await apiService.mealplan.getMealPlan(mealPlanId);

      // Fetch creator photo URL
      let creatorPhotoUrl: string | null = null;
      try {
        const photoResponse = await apiService.photo.getPhotoUrls({
          user_ids: [mealPlanData.creatorId]
        });
        creatorPhotoUrl = photoResponse.photo_urls[mealPlanData.creatorId] || null;
        logger.log('Creator photo URL fetched successfully:', creatorPhotoUrl);
      } catch (photoError) {
        logger.warn('Failed to fetch creator photo URL:', photoError);
      }

      // Add photo URL to meal plan
      const mealPlanWithPhoto = {
        ...mealPlanData,
        creatorPhotoUrl
      };

      logger.log('Meal plan refreshed successfully:', mealPlanWithPhoto);
      setMealPlan(mealPlanWithPhoto);

    } catch (err) {
      logger.error('Failed to refresh meal plan:', err);

      let errorMessage = 'Failed to load meal plan details. Please try again.';

      if (err instanceof MealApiError) {
        switch (err.status) {
          case 401:
            errorMessage = 'Authentication required. Please log in to continue.';
            break;
          case 403:
            errorMessage = 'You do not have permission to view this meal plan.';
            // Redirect to dashboard on 403 error
            navigate('/dashboard', { replace: true });
            return; // Exit early to prevent setting error state
          case 404:
            errorMessage = 'Meal plan not found.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = err.message || errorMessage;
        }
      }

      setError(errorMessage);
      setMealPlan(null);
    } finally {
      setLoading(false);
    }
  }, [mealPlanId, navigate]);

  /**
   * Automatically fetch meal plan when ID changes
   */
  useEffect(() => {
    if (mealPlanId) {
      fetchMealPlan();
    }
  }, [mealPlanId]); // Only depend on mealPlanId to prevent multiple calls

  return {
    mealPlan,
    loading,
    error,
    refresh,
  };
};

export default useMealPlanDetails;